import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AuthContext } from '../App';
import { FileText, PenLine, Linkedin, Briefcase, Mic, Sparkles, CalendarCheck, Mail, Target } from 'lucide-react';

const TOOLS = [
  { icon: FileText, title: 'ATS CV Checker', desc: 'Check your CV\u2019s ATS readiness score and get practical fixes.', plan: 'Free', to: '/cv' },
  { icon: PenLine, title: 'CV Creation & Optimization', desc: 'Build a new CV or professionally optimize your existing one for UAE roles.', plan: 'Pro', to: '/pricing' },
  { icon: Target, title: 'AI Job Match CV Optimizer', desc: 'Paste a target job title & description, upload your CV, and get it tailored to the role \u2014 using only your real experience.', plan: 'Career Boost', to: '/cv-checker' },
  { icon: Linkedin, title: 'LinkedIn Optimization', desc: 'Improve your headline, About section, experience and recruiter keywords.', plan: 'Career Boost', to: '/pricing' },
  { icon: Mail, title: 'Cover Letter & Application Tools', desc: 'Generate cover letters, email and WhatsApp application templates.', plan: 'Career Boost', to: '/pricing' },
  { icon: Briefcase, title: 'AI Mock Interview', desc: 'Role-based interview questions with detailed AI feedback and scoring.', plan: 'Elite', to: '/pricing' },
  { icon: Mic, title: 'AI Voice Interview', desc: 'Practice answering out loud and get evaluated on your responses.', plan: 'Elite', to: '/pricing' },
  { icon: Sparkles, title: 'Advanced AI Career Coach', desc: 'Personalized UAE job-search, CV, LinkedIn and interview guidance.', plan: 'Elite', to: '/pricing' },
  { icon: CalendarCheck, title: '30-Day Job Search Tracker', desc: 'A structured daily plan plus an application and follow-up log.', plan: 'Elite', to: '/pricing' },
];

const PLAN_BADGE = {
  Free: 'bg-gray-100 text-gray-700',
  Pro: 'bg-blue-100 text-blue-800',
  'Career Boost': 'bg-green-100 text-green-800',
  Elite: 'bg-red-100 text-red-800',
};

export default function CareerToolsPage() {
  const { user } = useContext(AuthContext);
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-14 max-w-6xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Career Tools to Improve Your UAE Job Search</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            From a free ATS CV check to AI interviews and personalized coaching — every tool is built for the UAE job market and unlocked by your package.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="career-tools-grid">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Card key={t.title} className="p-5 flex flex-col h-full hover:shadow-lg transition-all" data-testid={`career-tool-${t.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-11 h-11 rounded-lg bg-red-50 flex items-center justify-center text-[#CE1126]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_BADGE[t.plan] || 'bg-gray-100'}`}>{t.plan}</span>
                </div>
                <h2 className="font-semibold text-base mb-1">{t.title}</h2>
                <p className="text-sm text-gray-600 flex-1">{t.desc}</p>
                <Link to={t.to} className="mt-4">
                  <Button variant="outline" size="sm" className="w-full">{t.to === '/pricing' ? 'View packages' : (t.plan === 'Free' ? 'Try it free' : 'Open tool')}</Button>
                </Link>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Link to={user ? '/dashboard' : '/register'}>
            <Button className="btn-uae-red" size="lg" data-testid="career-tools-cta">
              {user ? 'Go to My Dashboard' : 'Get Started Free'}
            </Button>
          </Link>
          <p className="text-xs text-gray-500 mt-3">From CV to Interview, Your Trusted Career Partner.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
